/**
 * Pack builder — composition libre d'un pack de N t-shirts.
 *
 * MODÈLE : un SEUL produit (Couleur × Taille). Le pack n'est pas un produit,
 * c'est N lignes de panier posées d'un coup, reliées par une propriété
 * `_pack_id` commune. Conséquence directe : le stock reste juste, il n'y a
 * aucune fiche de pack à maintenir en double, et changer un prix se fait à un
 * seul endroit.
 *
 * UNE TAILLE POUR TOUT LE PACK, pas une par t-shirt. C'est ce que fait la
 * référence et c'est ce qui rend l'écran tenable : sur un pack de 4, on passe
 * de 8 décisions à 5.
 *
 * CE QUE CE COMPOSANT NE FAIT PAS, ET QUI SE VOIT AU CHECKOUT : il AFFICHE le
 * prix du pack, il ne l'APPLIQUE pas. Le prix réel vient d'une remise
 * automatique Shopify configurée dans l'admin. Sans elle, le client lit 75 € ici
 * et voit 84 € au checkout — la commande est perdue et la confiance avec. Le
 * bloc crie dans l'éditeur de thème tant que la remise n'est pas déclarée.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const OCCUPE = 'pack-builder--busy';

class PackBuilder extends HTMLElement {
  connectedCallback() {
    const source = this.querySelector('[data-pack-data]');
    if (!source) return;

    this.data = JSON.parse(source.textContent);
    this.tier = this.data.tiers[this.data.defaultTierIndex] ?? this.data.tiers[0];
    this.taille = this.#tailleParDefaut();
    // Un slot vide vaut null : on distingue « pas encore choisi » de « noir ».
    this.slots = new Array(this.tier.qty).fill(null);
    this.slotActif = 0;

    this.#onClick = this.#handleClick.bind(this);
    this.addEventListener('click', this.#onClick);
    this.#rendre();
  }

  disconnectedCallback() {
    if (this.#onClick) this.removeEventListener('click', this.#onClick);
  }

  #onClick = null;

  /** Première taille réellement disponible, sinon la première tout court. */
  #tailleParDefaut() {
    const dispo = this.data.sizes.find((t) =>
      this.data.variants.some((v) => v.size === t && v.available),
    );
    return dispo ?? this.data.sizes[0];
  }

  #handleClick(evenement) {
    const cible = evenement.target.closest('[data-act]');
    if (!cible || !this.contains(cible)) return;
    evenement.preventDefault();

    switch (cible.dataset.act) {
      case 'tier':
        this.#changerTier(Number(cible.dataset.qty));
        break;
      case 'size':
        this.taille = cible.dataset.size;
        break;
      case 'slot':
        this.slotActif = Number(cible.dataset.index);
        break;
      case 'color':
        this.#poserCouleur(cible.dataset.color);
        break;
      case 'clear':
        this.slots = new Array(this.tier.qty).fill(null);
        this.slotActif = 0;
        break;
      case 'add':
        this.#ajouterAuPanier();
        return;
      default:
        return;
    }
    this.#rendre();
  }

  /**
   * Changer de palier ne REPART PAS de zéro : les couleurs déjà choisies sont
   * conservées. Repartir de zéro punirait exactement le geste qu'on cherche à
   * provoquer — passer de 2 à 4.
   */
  #changerTier(qty) {
    const tier = this.data.tiers.find((t) => t.qty === qty);
    if (!tier) return;
    const anciens = this.slots.slice(0, qty);
    this.slots = anciens.concat(new Array(Math.max(0, qty - anciens.length)).fill(null));
    this.tier = tier;
    const premierVide = this.slots.indexOf(null);
    this.slotActif = premierVide === -1 ? this.slots.length - 1 : premierVide;
  }

  /** Remplit le slot actif puis avance sur le premier slot encore vide. */
  #poserCouleur(couleur) {
    this.slots[this.slotActif] = couleur;
    const suivant = this.slots.indexOf(null);
    if (suivant !== -1) this.slotActif = suivant;
  }

  #variante(couleur, taille) {
    return this.data.variants.find((v) => v.color === couleur && v.size === taille);
  }

  #complet() {
    return this.slots.every((c) => c !== null);
  }

  #formaterPrix(centimes) {
    const devise = window.Shopify?.currency?.active || this.data.currency || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
        style: 'currency',
        currency: devise,
      }).format(centimes / 100);
    } catch {
      return `${(centimes / 100).toFixed(2)} ${devise}`;
    }
  }

  #rendre() {
    const total = this.tier.unitPrice * this.tier.qty;
    const barre = this.data.basePrice * this.tier.qty;

    this.#rendreTiers();
    this.#rendreTailles();
    this.#rendreSlots();
    this.#rendrePalette();

    const compteur = this.querySelector('[data-pack-count]');
    if (compteur) {
      const choisis = this.slots.filter(Boolean).length;
      compteur.textContent = `${choisis}/${this.tier.qty}`;
    }

    const prix = this.querySelector('[data-pack-price]');
    if (prix) prix.textContent = this.#formaterPrix(total);
    const compare = this.querySelector('[data-pack-compare]');
    if (compare) {
      compare.textContent = barre > total ? this.#formaterPrix(barre) : '';
      compare.hidden = barre <= total;
    }

    const bouton = this.querySelector('[data-act="add"]');
    if (bouton) {
      const pret = this.#complet();
      bouton.disabled = !pret;
      const libelle = bouton.querySelector('[data-pack-cta-label]');
      if (libelle) {
        // Le jeton est `[n]` et NON `{{ count }}` : Shopify fait passer les
        // valeurs de réglages par Liquid, qui évalue `{{ count }}` comme une
        // variable inexistante et le remplace par du vide avant que ce code
        // le voie. Le libellé perdait son nombre en silence.
        libelle.textContent = pret
          ? this.data.labels.add
          : this.data.labels.incomplete.replace(
              '[n]',
              String(this.tier.qty - this.slots.filter(Boolean).length),
            );
      }
    }
  }

  #rendreTiers() {
    this.querySelectorAll('[data-act="tier"]').forEach((el) => {
      const actif = Number(el.dataset.qty) === this.tier.qty;
      el.classList.toggle('is-selected', actif);
      el.setAttribute('aria-pressed', String(actif));
    });
  }

  #rendreTailles() {
    this.querySelectorAll('[data-act="size"]').forEach((el) => {
      const taille = el.dataset.size;
      const actif = taille === this.taille;
      // Une taille est épuisée si AUCUNE couleur n'existe dans cette taille.
      const epuise = !this.data.variants.some((v) => v.size === taille && v.available);
      el.classList.toggle('is-selected', actif);
      el.classList.toggle('is-soldout', epuise);
      el.setAttribute('aria-pressed', String(actif));
    });
  }

  #rendreSlots() {
    const hote = this.querySelector('[data-pack-slots]');
    if (!hote) return;
    hote.innerHTML = '';
    this.slots.forEach((couleur, index) => {
      const bouton = document.createElement('button');
      bouton.type = 'button';
      bouton.className = 'pack-builder__slot';
      bouton.dataset.act = 'slot';
      bouton.dataset.index = String(index);
      bouton.classList.toggle('is-active', index === this.slotActif);
      bouton.classList.toggle('is-empty', couleur === null);
      const teinte = this.data.colors.find((c) => c.name === couleur);
      bouton.style.setProperty('--slot-swatch', teinte?.css || 'transparent');
      bouton.setAttribute(
        'aria-label',
        couleur
          ? `${this.data.labels.slot} ${index + 1} : ${couleur}`
          : `${this.data.labels.slot} ${index + 1} : ${this.data.labels.empty}`,
      );
      bouton.innerHTML = `<span class="pack-builder__slot-dot"></span><span class="pack-builder__slot-num">${index + 1}</span>`;
      hote.appendChild(bouton);
    });
  }

  #rendrePalette() {
    this.querySelectorAll('[data-act="color"]').forEach((el) => {
      const couleur = el.dataset.color;
      const variante = this.#variante(couleur, this.taille);
      const epuise = !variante || !variante.available;
      el.classList.toggle('is-soldout', epuise);
      el.disabled = epuise;
      const compte = this.slots.filter((c) => c === couleur).length;
      el.classList.toggle('is-used', compte > 0);
      const pastille = el.querySelector('[data-color-count]');
      if (pastille) {
        pastille.textContent = compte > 1 ? `×${compte}` : '';
        pastille.hidden = compte < 2;
      }
    });
  }

  async #ajouterAuPanier() {
    if (!this.#complet() || this.classList.contains(OCCUPE)) return;

    // Un identifiant partagé par les N lignes : c'est lui qui permettra au
    // panier de les afficher comme un pack plutôt que comme N achats séparés.
    const packId = `${this.tier.qty}-${Date.now().toString(36)}`;
    const parCouleur = new Map();
    for (const couleur of this.slots) {
      parCouleur.set(couleur, (parCouleur.get(couleur) || 0) + 1);
    }

    const items = [];
    for (const [couleur, quantite] of parCouleur) {
      const variante = this.#variante(couleur, this.taille);
      if (!variante) {
        this.#erreur(this.data.labels.unavailable);
        return;
      }
      items.push({
        id: Number(variante.id),
        quantity: quantite,
        // Le préfixe `_` masque la propriété au client tout en la gardant
        // visible sur la commande, côté préparation.
        properties: {
          _pack_id: packId,
          _pack_size: String(this.tier.qty),
        },
      });
    }

    this.classList.add(OCCUPE);
    this.#erreur('');
    try {
      const racine = window.Shopify?.routes?.root || '/';
      const ajout = await fetch(`${racine}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!ajout.ok) {
        const detail = await ajout.json().catch(() => null);
        throw new Error(detail?.description || `add.js ${ajout.status}`);
      }
      await ajout.json();

      const panier = await fetch(`${racine}cart.js`, { headers: { Accept: 'application/json' } });
      if (!panier.ok) throw new Error(`cart.js ${panier.status}`);
      const cart = await panier.json();

      // Même annonce que le reste du thème : sans `sections` dans la charge,
      // le cart drawer va chercher son HTML lui-même.
      const differe = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: differe.promise,
        }),
      );
      differe.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: 'pack-builder', itemCount: cart.item_count, items: cart.items },
      });
    } catch (erreur) {
      this.#erreur(erreur.message || this.data.labels.error);
    } finally {
      this.classList.remove(OCCUPE);
    }
  }

  #erreur(message) {
    const zone = this.querySelector('[data-pack-error]');
    if (!zone) return;
    zone.textContent = message;
    zone.hidden = !message;
  }
}

if (!customElements.get('pack-builder')) {
  customElements.define('pack-builder', PackBuilder);
}
